import { queryOptions } from "@tanstack/react-query";
import { httpPostGraphql } from "shared/api/httpClient";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { subdomain, useUserId } from "shared/stores/oidc";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";

/** @public */
export function useAddUserAddress() {
    const { userId } = useUserId();
    const { mutate: addUserAddress, mutateAsync: addUserAddressAsync } = useGraphqlMutation({
        document: mutations.addAddress,
        onSuccess: async () => {
            await qk.invalidate("user", "id", userId);
            await qk.invalidate("map", "customer", "userAddresses");
            notify.show.success({ message: "Added new address" });
        },
    });
    return { addUserAddress, addUserAddressAsync };
}

/** @public */
export function useEditUserAddress() {
    const { userId } = useUserId();
    const { mutate: editUserAddress, mutateAsync: editUserAddressAsync } = useGraphqlMutation({
        document: mutations.editAddress,
        onSuccess: async () => {
            await qk.invalidate("user", "id", userId);
            await qk.invalidate("map", "customer", "userAddresses");
            notify.show.success({ message: "Edited location" });
        },
    });
    return { editUserAddress, editUserAddressAsync };
}

/** @public */
export function useRemoveUserAddress() {
    const { userId } = useUserId();
    const { mutate: removeUserAddress, mutateAsync: removeUserAddressAsync } = useGraphqlMutation({
        document: mutations.removeAddress,
        onSuccess: async () => {
            await qk.invalidate("user", "id", userId);
            await qk.invalidate("map", "customer", "userAddresses");
            notify.show.success({ message: "Removed location" });
        },
    });
    return { removeUserAddress, removeUserAddressAsync };
}

/** @public */
export function useAddEud() {
    const { mutate: addEud, mutateAsync: addEudAsync } = useGraphqlMutation({
        document: mutations.addEud,
        onSuccess: async () => {
            await qk.invalidate("user");
        },
    });
    return { addEud, addEudAsync };
}

/** @public */
export function useEditEudName() {
    const { mutateAsync: editEudNameAsync } = useGraphqlMutation({
        document: mutations.editEudName,
        onSuccess: async () => {
            await qk.invalidate("user");
            notify.show.success({ message: "Updated nickname" });
        },
    });
    return { editEudNameAsync };
}

/** @public */
export function useVerifyEud() {
    const { mutateAsync: verifyEudAsync } = useGraphqlMutation({
        document: mutations.verifyEud,
        onSuccess: async () => {
            await qk.invalidate("user");
        },
    });
    return { verifyEudAsync };
}

/** @public */
export function useResendVerifyEud() {
    const { mutate: resendVerifyEud } = useGraphqlMutation({
        document: mutations.resendEud,
        onSuccess: () => notify.show.success({ message: "Resent verification code" }),
    });
    return { resendVerifyEud };
}

/** @public */
export function useRemoveEud() {
    const { mutate: removeEud, mutateAsync: removeEudAsync } = useGraphqlMutation({
        document: mutations.removeEud,
        onSuccess: async () => {
            await qk.invalidate("user");
            notify.show.success({ message: "Removed device" });
        },
    });
    return { removeEud, removeEudAsync };
}

/** @public */
export function useCreateUser(onSuccess?: (id?: string) => unknown) {
    const { mutateAsync: createUserAsync } = useGraphqlMutation({
        document: mutations.createUser,
        onSuccess: (res) => {
            onSuccess?.(res?.createUser.id);
        },
        onError: () => notify.show.error({ message: "Error updating user." }),
    });
    return { createUserAsync };
}

/** @public */
export function useUpdateUser(skipNotify?: boolean) {
    const { mutate: updateUser, mutateAsync: updateUserAsync } = useGraphqlMutation({
        document: mutations.updateUser,
        onSuccess: async () => {
            await qk.invalidate("user");
            !skipNotify && notify.show.success({ message: "Updated user." });
        },
        onError: () => notify.show.error({ message: "Error updating user." }),
    });
    return { updateUser, updateUserAsync };
}

/** @public */
export const customerQueryOptions = {
    whiteLabeling: queryOptions({
        staleTime: Infinity,
        queryKey: qk("customer", "whiteLabeling"),
        queryFn: () => httpPostGraphql(queries.whiteLabeling, { subdomain }),
    }),
};

const queries = {
    whiteLabeling: graphql(`
        query WhiteLabeling($subdomain: String) {
            customer(subDomain: $subdomain) {
                name
                id
            }
        }
    `),
};

const mutations = {
    createUser: graphql(`
        mutation CreateUser($email: String!, $name: String!) {
            createUser(email: $email, name: $name) {
                id
            }
        }
    `),
    updateUser: graphql(`
        mutation UpdateUser($userId: UUID!, $name: String!, $language: Language!) {
            updateUser(userId: $userId, params: { name: $name, language: $language }) {
                id
            }
        }
    `),
    addAddress: graphql(`
        mutation AddUserAddress($userId: UUID!, $address: UserAddressInput!) {
            addUserAddress(userId: $userId, address: $address) {
                id
            }
        }
    `),
    editAddress: graphql(`
        mutation EditUserAddress($userId: UUID!, $addressId: UUID!, $address: UserAddressInput!) {
            updateUserAddress(userId: $userId, addressId: $addressId, address: $address) {
                id
            }
        }
    `),
    removeAddress: graphql(`
        mutation RemoveUserAddress($userId: UUID!, $addressId: UUID!) {
            removeUserAddress(userId: $userId, addressId: $addressId)
        }
    `),
    addEud: graphql(`
        mutation AddEud($userId: UUID!, $params: EndUserDeviceParameterInput!) {
            addEUD(userId: $userId, eudParams: $params) {
                id
                value
                optOut {
                    sms
                    system
                }
            }
        }
    `),
    editEudName: graphql(`
        mutation EditEud($userId: UUID!, $eudId: UUID!, $name: String!) {
            updateEudName(userId: $userId, eudId: $eudId, name: $name) {
                id
                name
            }
        }
    `),
    verifyEud: graphql(`
        mutation VerifyEud($userId: UUID!, $eudId: UUID!, $code: String!) {
            verifyEUD(userId: $userId, eudId: $eudId, verificationCode: $code) {
                id
                isVerified
            }
        }
    `),
    resendEud: graphql(`
        mutation ResendEud($userId: UUID!, $eudID: UUID!) {
            resendEUDVerificationCode(userId: $userId, eudId: $eudID) {
                id
            }
        }
    `),
    removeEud: graphql(`
        mutation RemoveEud($userId: UUID!, $eudId: UUID!) {
            removeEUD(userId: $userId, eudId: $eudId)
        }
    `),
};
